import * as React from 'react'
import { IncomingSelfBillingOrder, IncomingSelfBillingOrderColumnConfig, IncomingSelfBillingOrderColumnProperty, IncomingSelfBillingOrderColumns, IncomingSelfBillingOrderProperty, IncomingSelfBillingOrderType, ResourceRendererOptions, ResourceRendererResult } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'
import IncomingSelfBillingOrderAvatar from '../Avatar/IncomingSelfBillingOrderAvatar'

export const DEFAULT_SELF_BILLING_ORDER_COLUMNS: IncomingSelfBillingOrderColumnProperty[] = [
  'number',
  'contact',
  'issue_date',
  'due_date',
  'net_total',
  'amount'
]

const SelfBillingOrderColumnConfigs: Record<IncomingSelfBillingOrderColumnProperty, IncomingSelfBillingOrderColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::id') }),
    render: ({ selfBillingOrder }) => selfBillingOrder.id || '-'
  },
  type: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::type') }),
    render: ({ selfBillingOrder }) => selfBillingOrder.type ? i18n.t(`IncomingSelfBillingOrderType::${selfBillingOrder.type}`) : '-'
  },
  contact: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::client') }),
    render: ({ selfBillingOrder }) => selfBillingOrder.contact?.name || '-'
  },
  number: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::number') }),
    render: ({ selfBillingOrder }) => selfBillingOrder.number || '-'
  },
  currency: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::currency') }),
    render: ({ selfBillingOrder }) => selfBillingOrder.currency || '-'
  },
  issue_date: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::issue_date') }),
    render: ({ selfBillingOrder, setting }) => selfBillingOrder.issue_date ? moment(selfBillingOrder.issue_date).format(setting.date_format) : '-'
  },
  due_date: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::due_date') }),
    render: ({ selfBillingOrder, setting }) => selfBillingOrder.due_date ? moment(selfBillingOrder.due_date).format(setting.date_format) : '-'
  },
  paid_on: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::paid_on') }),
    render: ({ selfBillingOrder, setting }) => selfBillingOrder.paid_on ? moment(selfBillingOrder.paid_on).format(setting.date_format) : '-'
  },
  booked_on: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::booked_on') }),
    render: ({ selfBillingOrder, setting }) => selfBillingOrder.booked_on ? moment(selfBillingOrder.booked_on).format(setting.date_format) : '-'
  },
  net_total: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::net_total'), align: 'right' }),
    render: ({ selfBillingOrder, setting }) => NumberFormatter.formatCurrency(selfBillingOrder.currency || setting.default_currency, setting.number_format, selfBillingOrder.net_total)
  },
  amount: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::amount'), align: 'right' }),
    render: ({ selfBillingOrder, setting }) => NumberFormatter.formatCurrency(selfBillingOrder.currency || setting.default_currency, setting.number_format, selfBillingOrder.amount)
  },
  remittance_information: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::remittance_information') }),
    render: ({ selfBillingOrder }) => selfBillingOrder.remittance_information || '-'
  },
  created_at: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::created_at') }),
    render: ({ selfBillingOrder, setting }) => selfBillingOrder.created_at ? moment(selfBillingOrder.created_at).format(setting.date_format) : '-'
  },
  updated_at: {
    header: () => ({ title: i18n.t('IncomingSelfBillingOrder::updated_at') }),
    render: ({ selfBillingOrder, setting }) => selfBillingOrder.updated_at ? moment(selfBillingOrder.updated_at).format(setting.date_format) : '-'
  },
}

export function getSelfBillingOrderTableRenderer(options: ResourceRendererOptions<IncomingSelfBillingOrder>): ResourceRendererResult<IncomingSelfBillingOrder, IncomingSelfBillingOrderProperty> {
  const {
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = IncomingSelfBillingOrderColumns.map(column => ({ label: i18n.t(`IncomingSelfBillingOrder::${column}`), value: column }))
  const selectedColumns: IncomingSelfBillingOrderProperty[] = LocalStorage
    .get(LocalStorageKey.INCOMING_SELF_BILLING_ORDER_COLUMNS, DEFAULT_SELF_BILLING_ORDER_COLUMNS)
    .filter((column: IncomingSelfBillingOrderColumnProperty) => SelfBillingOrderColumnConfigs[column])

  const headers: ResourceTableHeader[] =
    [
      {
        title: '',
      },
      ...selectedColumns
        .map(column => ({
          ...SelfBillingOrderColumnConfigs[column].header(),
          visible: true
        })),
      {
        title: '',
        stickyRight: '0px'
      }
    ]

  const renderRow = (selfBillingOrder: IncomingSelfBillingOrder, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(selfBillingOrder.id)
    let actions: IActionListItem[] = [
      { key: 'view', icon: 'eye', content: i18n.t('IncomingSelfBillingOrdersTable::View') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('IncomingSelfBillingOrdersTable::Delete'), destructive: true },
    ]

    const applyMaxWidthColumns: IncomingSelfBillingOrderProperty[] = [
      'contact',
      'number',
      'remittance_information'
    ]

    let cells: React.ReactNode[] =
      [
        <ResourceTableRowData onClick={onRowClick ? () => onRowClick(selfBillingOrder) : undefined} maxWidth='30px' maxWidthMobile='50px'>
          <IncomingSelfBillingOrderAvatar width={30} order={selfBillingOrder} rounded={true} onClick={onRowClick ? () => onRowClick(selfBillingOrder) : undefined} />
        </ResourceTableRowData>,
        ...selectedColumns.map((column) => (
          <ResourceTableRowData
            key={`${selfBillingOrder.id}-${column}`}
            onClick={onRowClick ? () => onRowClick(selfBillingOrder) : undefined}
            maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
            ellipse={applyMaxWidthColumns.includes(column)}
            textAlign={SelfBillingOrderColumnConfigs[column].header().align}
          >
            {SelfBillingOrderColumnConfigs[column].render({ selfBillingOrder, ...options })}
          </ResourceTableRowData>
        ))
      ]


    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${selfBillingOrder.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, selfBillingOrder)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={selfBillingOrder.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, selfBillingOrder.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getSelfBillingOrderTableRenderer
